import { Box, Flex, Text, Heading } from 'src/components/ui';
import { useContext, useState, useEffect } from 'react';
import UserContext from 'src/contexts/user/UserContext';
import { isEmpty } from 'lodash';
import Container from 'src/components/common/Container';
import DomainForm from '../scraping/DomainForm';
import { Campaigns } from '../projects';
import Sidebar from './Sidebar';
import NothingToSee from 'src/components/common/NothingToSee';
import BannerCarousel from './components/BannerCarousel';
import banners from './utils/banners';
import FusionLoading from 'src/components/common/FusionLoading';
import ShortcutGrid from './components/ShortcutGrid';
import ScrollToTop from 'src/components/common/ScrollToTop';
import TemplatesGallery from './components/TemplatesGallery';
import { createAbortController, cancelAllRequests } from 'src/services/http';
import { useRightSidebarContext } from 'src/contexts/app/RightSidebarContext';
import useCopilotChatContext from 'src/fusion-copilot/hooks/useCopilotChatContext';
import { useNewChat } from 'src/fusion-copilot/hooks/useNewChat';

const Home = () => {
	const [areCampaignsLoaded, setAreCampaignsLoaded] = useState(false);
	const { account } = useContext(UserContext);
	const [isDataLoading, setIsDataLoading] = useState(false);
	const [isNavigating, setIsNavigating] = useState(false);
	const [showOnboardingOverlay, setShowOnboardingOverlay] = useState(false);
	const { handleOpenSidebar } = useRightSidebarContext();
	const { openCopilot, messages } = useCopilotChatContext();
	const { handleNewChat, canStartNewChat } = useNewChat();

	const hasCompletedForm =
		!isEmpty(account?.name) && !isEmpty(account?.industry);

	const handleSubmitData = () => {
		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
	};

	// Open copilot for new accounts that haven't completed onboarding
	useEffect(() => {
		// Check if account hasn't completed onboarding (no name or industry set)
		const isNewAccount = !hasCompletedForm;
		const hasOpenedCopilotThisSession = sessionStorage.getItem('hasOpenedCopilot');

		// Wait for account to be loaded (ensures authentication is complete)
		// This prevents the copilot from opening during token refresh/re-authentication
		if (!account) {
			return;
		}

		if (isNewAccount && !hasOpenedCopilotThisSession && canStartNewChat) {
			// Show onboarding overlay and open copilot for new accounts
			setShowOnboardingOverlay(true);
			handleOpenSidebar('copilot');
			openCopilot(true); // Block screen
			sessionStorage.setItem('hasOpenedCopilot', 'true');

			// Only start a new chat if there are no messages (no existing conversation)
			// Otherwise, the CopilotChatProvider will automatically load the existing conversation
			if (messages.length === 0) {
				handleNewChat();
			}
		}
	}, [handleOpenSidebar, hasCompletedForm, canStartNewChat, handleNewChat, openCopilot, messages.length, account]);

	// Cleanup effect to prevent blocking when navigating away
	useEffect(() => {
		// Create a new abort controller for this page
		createAbortController();

		const handleBeforeUnload = () => {
			setIsNavigating(true);
			cancelAllRequests();
		};

		const handleNavigationStart = () => {
			setIsNavigating(true);
			cancelAllRequests();
		};

		// Listen for navigation events
		window.addEventListener('beforeunload', handleBeforeUnload);

		// Listen for route changes (React Router)
		const originalPushState = history.pushState;
		const originalReplaceState = history.replaceState;

		history.pushState = function(...args) {
			handleNavigationStart();
			return originalPushState.apply(history, args);
		};

		history.replaceState = function(...args) {
			handleNavigationStart();
			return originalReplaceState.apply(history, args);
		};

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			history.pushState = originalPushState;
			history.replaceState = originalReplaceState;
			// Cancel all requests when component unmounts
			cancelAllRequests();
		};
	}, []);

	// Close overlay when user completes onboarding
	useEffect(() => {
		if (hasCompletedForm && showOnboardingOverlay) {
			setShowOnboardingOverlay(false);
		}
	}, [hasCompletedForm, showOnboardingOverlay]);

	return (
		<>
			<ScrollToTop />
			{/* Onboarding overlay for new accounts - just the dark background, copilot sidebar handles the UI */}
			{showOnboardingOverlay && (
				<Box
					position="fixed"
					top={0}
					left={0}
					right={0}
					bottom={0}
					bg="blackAlpha.600"
					zIndex={40}
					onClick={() => setShowOnboardingOverlay(false)}
				/>
			)}
			<Flex w="full" direction="column" gap={5} maxW={1200} m="auto">
				<BannerCarousel images={banners} />
				<Flex
					w={{ base: 'full', lg: '75%', '2xl': '75%' }}
					alignSelf="center"
					gap={5}
					direction="column"
				>
					{' '}
					{/* {showFirstCampaignBanner && ( */}
					{/* <Container withGradientBorder>
							<Flex justify="space-between" w="full" alignItems="center">
								<VStack alignItems="flex-start">
									<Heading color="gray.900">Thank you</Heading>
									<Text>
										You can now generate your first campaign to promote your
										business
									</Text>
								</VStack>
								<Button
									variant="orangeSolid"
									onClick={() => navigate('/projects/campaigns/new')}
								>
									Promote my business
								</Button>
							</Flex>
						</Container> */}
					{/* )} */}
					{/* {!hasCompletedForm && (
						<Container withGradientBorder>
							<Heading color="gray.900">
								Tell us about your business and generate your first ad
							</Heading>
							<Text>
								This will allow FusionAds.ai to generate ads more accurately
								according to your the URL.
							</Text>
							<Box mt={3} w="full">
								<DomainForm
									withManualOption
									shouldShowForm
									onDataSubmit={handleSubmitData}
									isDataLoading={isDataLoading}
									setIsDataLoading={setIsDataLoading}
									buttonText="Obtain info"
								/>
							</Box>
						</Container>
					)}{' '} */}
					<Box my={5}>
						<Heading color="gray.900" mb={3}>
							How can we help you today?
						</Heading>
						<ShortcutGrid />
					</Box>
					{/* Only render heavy components if not navigating */}
					{!isNavigating && (
						<>
							<Box my={5} minHeight="290px">
								<TemplatesGallery />
							</Box>
							<Flex gap={5}>
								<Container minH="lg">
									<FusionLoading isLoading={!areCampaignsLoaded} />
									<Box
										display={areCampaignsLoaded ? 'block' : 'none'}
										width="100%"
										height="auto"
									>
										<Campaigns
											simplified
											withContainer={false}
											noCampaignMessage={NothingToSee}
											onCampaignsLoaded={() => setAreCampaignsLoaded(true)}
										/>
									</Box>
								</Container>
							</Flex>
						</>
					)}
					<Sidebar onClose={() => {}} />
				</Flex>
			</Flex>
		</>
	);
};
export default Home;
